import React, {useState} from "react"
import Select, {SingleValue} from "react-select"
import useAsyncResultIdle from "../../../hooks/useAsyncResultIdle"
import {DepositAccount, Wallet} from "../../../resources/account"
import {useAccessToken} from "../../../services/auth"
import {Address} from "../../../resources/common"
import {createClosureCheckPayment} from "../../../resources/checkPayments"
import {getCustomerName, IndividualCustomer} from "../../../resources/customer"
import Icon from "../../../components/Icon/Icon"
import {Modal} from "../../../components/Modals/Modal"
import Button from "../../../components/Button/Button"
import CheckMailingAddress from "./CheckMailingAddress"
import {handleSubmit, refreshAndClose} from "./Utils"
import {BusinessPayeeOption, CheckPaymentWithStopPayment} from "./CreateClosureCheck"

const MAX_PAYEE_CHARS = 40
const JOINT_DELIMITER = " AND "

type PayeeOption = BusinessPayeeOption

interface NewClosureCheckModalProps {
	account: DepositAccount | Wallet
	customerId: string
	payeeName: string
	accountHolders?: IndividualCustomer[]
	businessPayeeOptions?: PayeeOption[]
	updatedAddress: Address
	openChangeAddressModal: () => void
	closureChecks?: {match: Function} | CheckPaymentWithStopPayment[]
	closeModal: () => void
	onSuccess: () => void
}

export function NewClosureCheckModal({
	account,
	customerId,
	payeeName,
	accountHolders,
	businessPayeeOptions,
	updatedAddress,
	openChangeAddressModal,
	closureChecks,
	closeModal,
	onSuccess,
}: NewClosureCheckModalProps) {
	const accessToken = useAccessToken()
	const accountId = account.id
	const [, create] = useAsyncResultIdle(createClosureCheckPayment)

	const [isLoading, setLoading] = useState(false)
	const [successMessage, setSuccessMessage] = React.useState("")
	const [errorMessage, setErrorMessage] = React.useState("")

	// Joint account payee state
	const [selectedHolderIds, setSelectedHolderIds] = useState<string[]>([])
	const jointPayeeName = React.useMemo(() => {
		if (!accountHolders) return undefined
		const names = selectedHolderIds
			.map((id) => accountHolders.find((h) => h.id === id))
			.filter((h): h is IndividualCustomer => h != null)
			.map((h) => getCustomerName(h))
		return names.length > 0 ? names.join(JOINT_DELIMITER) : undefined
	}, [selectedHolderIds, accountHolders])
	const jointCharCount = jointPayeeName?.length ?? 0
	const jointOverLimit = jointCharCount > MAX_PAYEE_CHARS

	// Business account payee state
	const [selectedBusinessPayee, setSelectedBusinessPayee] = useState<PayeeOption | null>(null)

	// Helper function to calculate total from checks array
	const calculateChecksTotal = (checks: CheckPaymentWithStopPayment[]) => {
		return checks.reduce((total, check) => {
			if (!check.stopPaymentStatus || check.stopPaymentStatus !== "Active") {
				return total + check.attributes.amount
			}
			return total
		}, 0)
	}

	// Calculate total of previous checks
	const previousChecksTotal = React.useMemo(() => {
		if (!closureChecks) return 0

		if (Array.isArray(closureChecks)) {
			return calculateChecksTotal(closureChecks)
		}

		return (
			closureChecks.match?.(
				() => 0,
				(checks: {data: CheckPaymentWithStopPayment[]}) => calculateChecksTotal(checks.data),
				() => 0
			) || 0
		)
	}, [closureChecks])

	// Initialize check amount as account balance minus previous checks
	const initialCheckAmount = React.useMemo(() => {
		const remainingBalance = Math.max(0, account.attributes.available - previousChecksTotal)
		return (remainingBalance / 100).toFixed(2)
	}, [account.attributes.balance, previousChecksTotal])

	const [checkAmount, setCheckAmount] = useState<string>(initialCheckAmount)

	// Update check amount when initial amount changes
	React.useEffect(() => {
		setCheckAmount(initialCheckAmount)
	}, [initialCheckAmount])

	// Calculate maximum possible check amount (fixed value)
	const maxCheckAmount = Math.max(0, account.attributes.balance - previousChecksTotal)

	// Check if entered amount exceeds available balance
	const enteredAmountCents = Math.round((parseFloat(checkAmount) || 0) * 100)
	const amountExceedsBalance = enteredAmountCents > maxCheckAmount

	const onSubmit = () => {
		const checkAmountCents = enteredAmountCents
		const effectivePayeeName = accountHolders
			? jointPayeeName
			: businessPayeeOptions
			? selectedBusinessPayee?.payeeName
			: undefined
		handleSubmit({
			createFn: create,
			params: {
				accessToken,
				accountId,
				customerId,
				addressOverride: updatedAddress,
				amount: checkAmountCents,
				payeeName: effectivePayeeName,
			},
			setLoading,
			setSuccessMessage,
			setErrorMessage,
			onSuccess,
			closeModal,
			successMessage: "Check issued successfully",
		})
	}

	const isSubmitDisabled =
		amountExceedsBalance ||
		isLoading ||
		(accountHolders != null && (selectedHolderIds.length === 0 || jointOverLimit)) ||
		(businessPayeeOptions != null && selectedBusinessPayee == null)

	// return class name and icon for stop payment status
	const renderStopPaymentIcon = (stopPaymentStatus?: string) => {
		if (stopPaymentStatus === "Active") {
			return <Icon icon={"stop-payment"} color={"#FFFFFF"} size={16} />
		} else if (stopPaymentStatus === "Disabled") {
			return <Icon icon={"cancel-stop-payment"} size={16} />
		}
		return null
	}

	const renderChecksList = (checks: CheckPaymentWithStopPayment[]) => {
		if (checks.length === 0) {
			return (
				<div className="no-checks-list">
					<div className="no-prev-checks">
						<span>No previously issued checks</span>
					</div>
				</div>
			)
		}

		return (
			<div className="checks-list">
				{checks.map((c) => (
					<div key={c.id} className="check-list-item">
						<div className="check-info">
							<span className="check-number">{`Check #${c.attributes.checkNumber}`}</span>
							{renderStopPaymentIcon(c.stopPaymentStatus)}
						</div>
						<div className="check-amount">
							{c.stopPaymentStatus === "Active" ? (
								<span className="amount-with-cover">${(c.attributes.amount / 100).toFixed(2)}</span>
							) : (
								<span>${(c.attributes.amount / 100).toFixed(2)}</span>
							)}
						</div>
					</div>
				))}
			</div>
		)
	}

	const renderPreviousChecks = () => {
		if (!closureChecks) return null

		if (Array.isArray(closureChecks)) {
			return renderChecksList(closureChecks)
		}

		return closureChecks.match?.(
			() => null,
			(checks: {data: CheckPaymentWithStopPayment[]}) => {
				return renderChecksList(checks.data)
			},
			() => null
		)
	}

	return (
		<Modal
			classname="closure-check-modal"
			close={() => refreshAndClose(onSuccess, closeModal)}
			enableClickAway={true}
			blockBodyScroll={true}
		>
			<div className="modal-content">
				<h3 className="title is-4">Issue Closure check for account #{accountId}</h3>
				<p>A check for the entered amount will be sent to the following address. This action cannot be undone.</p>

				<div className="modal-body-content">
					<div className="prev-checks">
						<h3 className="title is-6 is-bold">Previously issued Closure Checks</h3>
						{renderPreviousChecks()}
					</div>

					{/* Check Amount Section */}
					<div className="check-amount-section">
						<h3 className="title is-6 is-bold">Check Amount</h3>
						<div className="check-amount-input-container">
							<div className={`field has-addons ${amountExceedsBalance ? "has-error" : ""}`}>
								<div className="control">
									<span className="currency-symbol">$</span>
								</div>
								<div className="control is-expanded input-with-balance">
									<input
										className="input check-amount-input"
										type="text"
										value={checkAmount}
										onChange={(e) => setCheckAmount(e.target.value)}
										placeholder={(maxCheckAmount / 100).toFixed(2)}
									/>
									<div className="available-balance-inside">
										Available for Check Issuance ${(maxCheckAmount / 100).toFixed(2)}
									</div>
								</div>
							</div>
							{amountExceedsBalance && (
								<div className="error-message-below">Amount exceeds available check issuance balance</div>
							)}
						</div>
					</div>

					{/* Payee Section */}
					{accountHolders ? (
						// Joint account: multi-select
						<div className="payee-section">
							<h3 className="title is-6 is-bold">Payee</h3>
							<Select<PayeeOption, true>
								isMulti
								options={accountHolders.map((h) => ({
									value: h.id,
									label: getCustomerName(h),
									payeeName: getCustomerName(h),
								}))}
								value={accountHolders
									.filter((h) => selectedHolderIds.includes(h.id))
									.map((h) => ({value: h.id, label: getCustomerName(h), payeeName: getCustomerName(h)}))}
								onChange={(selected) => setSelectedHolderIds(selected.map((s) => s.value))}
								placeholder="Select one or more account holders"
								className={`payee-multi-select${jointOverLimit ? " has-error" : ""}`}
								classNamePrefix="payee-select"
							/>
							<div className={`payee-char-row${jointOverLimit ? " has-error" : ""}`}>
								{jointOverLimit && (
									<span className="payee-error">Payees exceed character limit. Remove 1 or more payees.</span>
								)}
								{jointPayeeName && !jointOverLimit && (
									<span className="payee-prints-as">
										Prints on the check as <strong>&quot;{jointPayeeName}&quot;</strong>
									</span>
								)}
								<span className="payee-char-count">
									{jointCharCount}/{MAX_PAYEE_CHARS} characters
								</span>
							</div>
						</div>
					) : businessPayeeOptions ? (
						// Business account: single-select (options pre-fetched by CreateClosureCheckModal)
						<div className="payee-section">
							<h3 className="title is-6 is-bold">Payee</h3>
							<Select<PayeeOption, false>
								isMulti={false}
								options={businessPayeeOptions}
								value={selectedBusinessPayee}
								onChange={(opt: SingleValue<PayeeOption>) => setSelectedBusinessPayee(opt)}
								placeholder="Select payee"
								className="payee-single-select"
								classNamePrefix="payee-select"
								isClearable
							/>
							{selectedBusinessPayee && (
								<div className="payee-prints-as">
									Prints on the check as <strong>&quot;{selectedBusinessPayee.payeeName}&quot;</strong>
								</div>
							)}
						</div>
					) : (
						// Individual account: read-only
						<div className="payee-section">
							<h3 className="title is-6 is-bold">Payee</h3>
							<div className="payee-readonly">{payeeName}</div>
						</div>
					)}

					<CheckMailingAddress address={updatedAddress} openChangeAddressModal={openChangeAddressModal} />
				</div>

				<div className="modal-actions is-flex is-justify-content-end">
					<Button className="button is-light transparent" theme={"neutral"} onClick={closeModal}>
						Cancel
					</Button>
					<Button className="button is-black wider" loading={isLoading} onClick={onSubmit} disabled={isSubmitDisabled}>
						Issue Check
					</Button>
					{successMessage && <p className={"success-message"}>{successMessage}</p>}
					{errorMessage && <p className={"error-message"}>{errorMessage}</p>}
				</div>
			</div>
		</Modal>
	)
}
